import { WeatherTableHeader } from '../WeatherTableHeader/WeatherTableHeader.component';
import { MetricRow } from './components/MetricRow.component';
import { WeatherChart } from './components/WeatherChart.component';
import { MeteoHomeContext } from 'pages/meteo/[type]/[locality]';
import { ForecastParamCode } from 'pleinchamp-api-client';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FlatButtonVariant } from '@components/buttons/Buttons.type';
import { PlcButtonGroup } from '@layout/components/PlcButtonGroup/PlcButtonGroup.component';
import { useWeatherLink } from '@meteo/business/meteoUrl.utils';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { Spacing } from '@styles/Spacing.style';
import { isServer } from '@utils/server';
import { urlEnum } from '@utils/url';
import classNames from 'classnames';
import {
  Direction,
  filterParamsToDisplay,
  getScroll,
  MetricsType,
  MetricsType24H,
  NUMBER_OF_DEFAULT_METRICS,
  WEATHER_TABLE_CELL_WIDTH,
  WeatherTableContentProps,
} from '../utils';

import '../WeatherTable.scss';

export function WeatherTableContent({
  dates,
  weatherCodes,
  isChartEnabled,
  format15Days,
  metrics,
  displayHHLink,
  timeframe,
  thirtySixHours,
  withDays,
  isSeeMoreEnabledDefault = false,
  isLoading,
  top,
  coordinates,
  ...rest
}: WeatherTableContentProps) {
  const isSmartphone = useIsBreakpointDown(Breakpoint.m);
  const overFlowingContainerRef = useRef<HTMLDivElement | null>(null);
  const hourByHourLinkProps = useWeatherLink(urlEnum.hourByHour);
  const [isSeeMoreEnabled, setSeeMore] = useState<boolean>(
    isSeeMoreEnabledDefault || metrics.length < NUMBER_OF_DEFAULT_METRICS
  );

  const { city } = useContext(MeteoHomeContext);

  useEffect(() => {
    setSeeMore(false);
  }, [city]);

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>();

  const hasChart = isChartEnabled && !isSmartphone && !isLoading;

  function handleNavigation(direction: Direction) {
    if (!overFlowingContainerRef.current) {
      return;
    }

    const scrollStep = WEATHER_TABLE_CELL_WIDTH * 4;
    const offset = getScroll(overFlowingContainerRef, direction);

    if (!offset) {
      return;
    }

    if (direction === Direction.Left) {
      overFlowingContainerRef.current.scrollTo({
        behavior: 'smooth',
        left:
          Math.round(overFlowingContainerRef.current.scrollLeft / WEATHER_TABLE_CELL_WIDTH) * WEATHER_TABLE_CELL_WIDTH -
          scrollStep,
      });
    }
    if (direction === Direction.Right) {
      overFlowingContainerRef.current.scrollTo({
        behavior: 'smooth',
        left:
          Math.round(overFlowingContainerRef.current.scrollLeft / WEATHER_TABLE_CELL_WIDTH) * WEATHER_TABLE_CELL_WIDTH +
          scrollStep +
          Spacing.m,
      });
    }
  }

  const onScroll = useCallback(() => {
    // The sticky component doesn't allow to get a ref on the inner wrapper thus we need to query it
    const tableHeaderScrollingElement = overFlowingContainerRef?.current?.querySelector('.sticky-inner-wrapper');
    if (tableHeaderScrollingElement) {
      tableHeaderScrollingElement.scroll({
        behavior: 'auto',
        left: overFlowingContainerRef?.current?.scrollLeft,
      });
    }
  }, []);

  if (isServer()) {
    // SSR & sticky header, not a good match…
    return null;
  }

  return (
    <div className={classNames('forecast-table', { thirtySixHours, withDays })}>
      <div ref={overFlowingContainerRef} className="overflowing-container" onScroll={onScroll}>
        <WeatherTableHeader
          coordinates={coordinates}
          dates={dates}
          format15Days={format15Days}
          handleNavigation={handleNavigation}
          hoveredIndex={hoveredIndex}
          isLoading={isLoading}
          overFlowingContainerRef={overFlowingContainerRef}
          setHoveredIndex={setHoveredIndex}
          top={top}
          weatherCodes={weatherCodes}
          withDays={withDays}
        />
        {/* <BecomeProOverlay15d asOverlay i18nTitle="actu.become-pro.15d-title" visible={format15Days && !isLoading} /> */}
        <table {...rest} onMouseLeave={() => setHoveredIndex(undefined)}>
          <tbody>
            {hasChart && (
              <WeatherChart
                hoveredIndex={hoveredIndex}
                is15Days={format15Days}
                is36h={thirtySixHours}
                metrics={metrics}
                onMouseEnter={setHoveredIndex}
              />
            )}
            {!hasChart && !format15Days && (
              <MetricRow
                hoveredIndex={hoveredIndex}
                is15Days={format15Days}
                is36h={thirtySixHours}
                isLoading={isLoading}
                metric={(metrics as Array<MetricsType>).find(m => m.key === ForecastParamCode.AirTemperatureInCelsius)}
                onMouseEnter={setHoveredIndex}
              />
            )}
            {(metrics as Array<MetricsType | MetricsType24H>)
              .filter(filterParamsToDisplay)
              .slice(0, isSeeMoreEnabled || metrics.length < NUMBER_OF_DEFAULT_METRICS ? metrics.length : 11)
              .map(metric => (
                <MetricRow
                  key={metric.key}
                  hoveredIndex={hoveredIndex}
                  is15Days={format15Days}
                  is36h={thirtySixHours}
                  isLoading={isLoading}
                  metric={metric}
                  onMouseEnter={setHoveredIndex}
                />
              ))}
          </tbody>
        </table>
      </div>
      <PlcButtonGroup
        buttons={[
          isSeeMoreEnabled
            ? undefined
            : {
                onClick: () => setSeeMore(true),
                textI18nKey: 'meteo.see-more-metrics',
                variant: FlatButtonVariant.secondary,
              },
          displayHHLink
            ? {
                linkProps: hourByHourLinkProps,
                textI18nKey: 'meteo.hour-by-hour-button',
                variant: FlatButtonVariant.primary,
                wrapperClassName: 'flex-1 flex-0-up-s',
              }
            : undefined,
        ]}
        className="plc-button-group-weather-table"
        style={{ justifyContent: 'flex-end' }}
      />
    </div>
  );
}
